import { User, CreateUserData, UpdateUserData, UserFilters, UsersResponse, CreateUserResponse } from '../types/user';
import { Config, getToken } from './config';

class UserService {
  private getHeaders(): HeadersInit {
    const token = getToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async getUsers(filters?: UserFilters): Promise<UsersResponse> {
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.tipo) params.append('tipo', filters.tipo);
    if (filters?.ativo !== undefined) params.append('ativo', filters.ativo.toString());

    const queryString = params.toString();
    const url = `${Config.API_BASE_URL}/usuarios${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(url, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao buscar usuários' }));
      throw new Error(error.error || error.message || 'Erro ao buscar usuários');
    }

    return response.json();
  }

  async getUserById(id: number): Promise<User> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${id}`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao buscar usuário' }));
      throw new Error(error.error || error.message || 'Erro ao buscar usuário');
    }

    return response.json();
  }

  async createUser(data: CreateUserData): Promise<CreateUserResponse> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao criar usuário' }));
      throw new Error(error.error || error.message || 'Erro ao criar usuário');
    }

    return response.json();
  }

  async updateUser(id: number, data: UpdateUserData): Promise<User> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${id}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao atualizar usuário' }));
      throw new Error(error.error || error.message || 'Erro ao atualizar usuário');
    }

    return response.json();
  }

  async deactivateUser(id: number): Promise<void> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${id}`, {
      method: 'DELETE',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao desativar usuário');
    }
  }

  async reactivateUser(id: number): Promise<void> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${id}/reativar`, {
      method: 'PATCH',
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao reativar usuário');
    }
  }

  async getFiliais(): Promise<{ success: boolean; filiais: { id: number; razao_social: string; nome_fantasia?: string; cnpj: string }[] }> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/dados/filiais`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao buscar filiais' }));
      throw new Error(error.error || error.message || 'Erro ao buscar filiais');
    }

    return response.json();
  }

  async getClientesVinculados(userId: number): Promise<{ success: boolean; clientes: { cliente_id: number; razao_social: string; cnpj: string }[] }> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${userId}/clientes-vinculados`, {
      headers: this.getHeaders()
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao buscar clientes vinculados' }));
      throw new Error(error.error || error.message || 'Erro ao buscar clientes vinculados');
    }

    return response.json();
  }

  async resetPassword(id: number, enviarEmail: boolean = false): Promise<{ senha_temporaria?: string; email_enviado: boolean }> {
    const response = await fetch(`${Config.API_BASE_URL}/usuarios/${id}/resetar-senha`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ enviar_email: enviarEmail })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Erro ao resetar senha' }));
      throw new Error(error.error || error.message || 'Erro ao resetar senha');
    }

    // 204 No Content não tem body
    if (response.status === 204) {
      return { email_enviado: enviarEmail };
    }

    return response.json();
  }
}

const userService = new UserService();
export default userService;
